import React, { useEffect } from "react";
import {
  LayoutDashboard,
  Grid3x3,
  Layers,
  BarChart3,
  Bell,
  CheckCircle2,
} from "lucide-react";
import HeaderMenu from "../../Navbar/headermenu";
import CustomFooter from "../../Footer/customFooter";
import { Helmet } from "react-helmet";

import "../../../assets/css/CustomDashboardFeature.css";

import dashboardToday from "../../../assets/new-images/Dashboard.png";
import dashboardTasks from "../../../assets/new-images/DashboardHome.png";

const features = [
  {
    icon: <Grid3x3 size={26} />,
    title: "Widget-Based Layout",
    description:
      "Today's tasks, performance, events, and reminders — each a focused widget on one dashboard.",
  },
  {
    icon: <Layers size={26} />,
    title: "Multiple Dashboards",
    description:
      "Create and switch between multiple dashboards for different teams or workflows.",
  },
  {
    icon: <BarChart3 size={26} />,
    title: "Live Task Breakdown",
    description:
      "To Do, In Progress, Under Review, and Closed counts, updated automatically as work moves.",
  },
  {
    icon: <Bell size={26} />,
    title: "Reminders & Events",
    description:
      "Upcoming deadlines, birthdays, and events surfaced right on your dashboard, not buried in a calendar.",
  },
];

const showcase = [
  {
    eyebrow: "Your Day, At a Glance",
    title: "Everything that needs your attention today",
    description:
      "See today's task movement — added, closed, exited — against where you started the day, plus a simple performance percentage and any reminders due, without opening a single task.",
    image: dashboardToday,
    alt: "Simplifyte dashboard showing today's date, today's tasks with start-of-day and current pending counts, today's performance percentage, and upcoming events and reminders",
  },
  {
    eyebrow: "Task Breakdown",
    title: "See where every task stands, instantly",
    description:
      "Pending and closed totals, plus a live bar chart across To Do, In Progress, Under Review, and Closed — so status updates stop being a message you have to send.",
    image: dashboardTasks,
    alt: "Simplifyte dashboard showing pending and closed task totals, a bar chart of tasks by status, and meeting and reminder widgets",
  },
];

const benefits = [
  "See today's task movement — added, closed, exited — without opening a single task",
  "Track performance with a simple, always-current completion percentage",
  "Switch between multiple dashboards for different teams or workflows",
  "Catch upcoming reminders and events before they're overdue",
  "One glance replaces a dozen status-check messages",
];

export default function CustomDashboardFeature() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Custom Dashboards | Simplifyte</title>
      </Helmet>
      <HeaderMenu />
      <div className="CustomDashboard_class_main">
        {/* Hero */}
        <section className="CustomDashboard_class_hero">
          <div className="CustomDashboard_class_container">
            <div className="CustomDashboard_class_hero_content">
              <div className="CustomDashboard_class_icon_box">
                <LayoutDashboard size={34} />
              </div>

              <h1 className="CustomDashboard_class_heading">
                A dashboard built around your day
              </h1>

              <p className="CustomDashboard_class_subheading">
                See exactly what needs attention — tasks, performance,
                events, and reminders, all in one view.
              </p>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="CustomDashboard_class_features_section">
          <div className="CustomDashboard_class_container">
            <h2 className="CustomDashboard_class_section_title CustomDashboard_class_center">
              Everything on one screen
            </h2>

            <div className="CustomDashboard_class_features_grid">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className="CustomDashboard_class_feature_card"
                >
                  <div className="CustomDashboard_class_feature_icon">
                    {feature.icon}
                  </div>

                  <h3 className="CustomDashboard_class_feature_title">
                    {feature.title}
                  </h3>

                  <p className="CustomDashboard_class_feature_description">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Product Showcase */}
        <section className="CustomDashboard_class_showcase_section">
          <div className="CustomDashboard_class_container">
            <h2 className="CustomDashboard_class_section_title CustomDashboard_class_center">
              See your dashboard in action
            </h2>
            <p className="CustomDashboard_class_showcase_subtitle">
              Real screens from the product — the same dashboard you'd see
              the moment you log in.
            </p>

            <div className="CustomDashboard_class_showcase_list">
              {showcase.map((item, index) => (
                <div
                  key={index}
                  className={`CustomDashboard_class_showcase_row ${
                    index % 2 === 1
                      ? "CustomDashboard_class_showcase_row_reverse"
                      : ""
                  }`}
                >
                  <div className="CustomDashboard_class_showcase_text">
                    <span className="CustomDashboard_class_showcase_eyebrow">
                      {item.eyebrow}
                    </span>
                    <h3 className="CustomDashboard_class_showcase_title">
                      {item.title}
                    </h3>
                    <p className="CustomDashboard_class_showcase_description">
                      {item.description}
                    </p>
                  </div>

                  <div className="CustomDashboard_class_showcase_media">
                    <div className="CustomDashboard_class_showcase_frame_outer">
                      <div className="CustomDashboard_class_showcase_frame_inner">
                        <img
                          src={item.image}
                          alt={item.alt}
                          loading="lazy"
                          className="CustomDashboard_class_showcase_image"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="CustomDashboard_class_benefits_section">
          <div className="CustomDashboard_class_container">
            <div className="CustomDashboard_class_benefits_wrapper">
              <h2 className="CustomDashboard_class_section_title CustomDashboard_class_center">
                Why teams start their day on the dashboard
              </h2>

              <div className="CustomDashboard_class_benefits_list">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    className="CustomDashboard_class_benefit_item"
                  >
                    <CheckCircle2
                      size={24}
                      className="CustomDashboard_class_check_icon"
                    />

                    <p>{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="CustomDashboard_class_cta_section">
          <div className="CustomDashboard_class_container">
            <div className="CustomDashboard_class_cta_box">
              <h2 className="CustomDashboard_class_cta_title">
                Ready to see your day at a glance?
              </h2>

              <p className="CustomDashboard_class_cta_text">
                Join teams who start their day on a dashboard, not a search
                through old messages.
              </p>

              <button
                className="CustomDashboard_class_cta_button"
                onClick={() =>
                  (window.location.href = "https://app.simplifyte.com")
                }
              >
                Start Free Trial
              </button>
            </div>
          </div>
        </section>
      </div>
      <CustomFooter />
    </>
  );
}
